import {
  SOCIAL_PLATFORMS,
  SocialPlatform,
  SocialTopicBrandedTotals,
  SocialTopicCountCell,
  SocialTopicCountsResponse,
  SocialTopicFilters,
  SocialTopicMetric,
  SocialTopicPostItem,
  SocialTopicSeriesBucket,
  SocialWindow,
} from "./social-listening";

/**
 * The Topic page's number rules, shared by the page and the Assistant's Topic
 * reads (plan D5), so both print the same values from the same counts. Pure
 * functions only, moved unchanged from the frontend chart files:
 * - buckets are UTC calendar days or weeks, `YYYY-MM-DD`, gap-filled;
 * - shares of a split use largest remainder, so shown parts sum to exactly 100;
 * - a missing or zero denominator gives `null` (shown as n/a), never NaN or
 *   Infinity;
 * - rankings break ties by label, then key, so the order never flickers.
 */

export type SocialSeriesBucketSize = "day" | "week";

/**
 * One `/series` row (API contract, Step 1): a bucket and channel with posts.
 * Structural, so the shared response type passes straight in.
 */
export interface SocialSeriesRowInput {
  start: string;
  platform: SocialPlatform;
  posts: number;
  positive: number;
  negative: number;
  neutral: number;
  mixed: number;
  brandPosts?: number;
  brandedPosts?: number;
  /** Summed views of the row's posts; absent on servers before the measures. */
  views?: number;
  /** Posts of the row that report views. */
  viewsPosts?: number;
  /** Kept comments dated in the bucket (D23); absent on servers before it. */
  comments?: number;
}

export interface SocialSeriesCounts {
  posts: number;
  positive: number;
  negative: number;
  neutral: number;
  mixed: number;
  brandPosts: number;
  brandedPosts: number;
  views: number;
  viewsPosts: number;
  /** Kept comments dated in the bucket: activity beside the posts, never in them. */
  comments: number;
}

export interface SocialFilledBucket {
  start: string;
  total: SocialSeriesCounts;
  platforms: Record<SocialPlatform, SocialSeriesCounts>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function emptyCounts(): SocialSeriesCounts {
  return {
    posts: 0,
    positive: 0,
    negative: 0,
    neutral: 0,
    mixed: 0,
    brandPosts: 0,
    brandedPosts: 0,
    views: 0,
    viewsPosts: 0,
    comments: 0,
  };
}

function finiteCount(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return value;
}

function utcDayMs(day: string): number | null {
  if (!ISO_DAY.test(day)) {
    return null;
  }
  const ms = Date.parse(`${day}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Bucket starts from `first` to `last` inclusive, stepping one UTC day or week. */
export function bucketStarts(
  first: string,
  last: string,
  bucket: SocialSeriesBucketSize,
): Array<string> {
  const from = utcDayMs(first);
  const to = utcDayMs(last);
  if (from == null || to == null || from > to) {
    return [];
  }
  const step = bucket === "week" ? 7 * DAY_MS : DAY_MS;
  const starts: Array<string> = [];
  for (let ms = from; ms <= to; ms += step) {
    starts.push(isoDay(ms));
  }
  return starts;
}

function addRow(target: SocialSeriesCounts, row: SocialSeriesRowInput) {
  target.posts += finiteCount(row.posts);
  target.positive += finiteCount(row.positive);
  target.negative += finiteCount(row.negative);
  target.neutral += finiteCount(row.neutral);
  target.mixed += finiteCount(row.mixed);
  target.brandPosts += finiteCount(row.brandPosts);
  target.brandedPosts += finiteCount(row.brandedPosts);
  target.views += finiteCount(row.views);
  target.viewsPosts += finiteCount(row.viewsPosts);
  target.comments += finiteCount(row.comments);
}

function emptyBucket(start: string): SocialFilledBucket {
  const platforms = {} as Record<SocialPlatform, SocialSeriesCounts>;
  for (const platform of SOCIAL_PLATFORMS) {
    platforms[platform] = emptyCounts();
  }
  return { start, total: emptyCounts(), platforms };
}

/**
 * The series as one entry per bucket, from the first to the last (or across
 * `range`), with empty buckets and channels filled with zeros.
 */
export function fillBuckets(
  rows: Array<SocialSeriesRowInput>,
  bucket: SocialSeriesBucketSize,
  range?: { from: string; to: string },
): Array<SocialFilledBucket> {
  const valid = rows.filter((row) => utcDayMs(row.start) != null);
  const sortedStarts = valid.map((row) => row.start).sort();
  const first = range?.from ?? sortedStarts[0];
  const last = range?.to ?? sortedStarts[sortedStarts.length - 1];
  if (first == null || last == null) {
    return [];
  }
  const buckets = bucketStarts(first, last, bucket).map(emptyBucket);
  const byStart = new Map(buckets.map((entry) => [entry.start, entry]));
  for (const row of valid) {
    const entry = byStart.get(row.start);
    if (entry == null || !(row.platform in entry.platforms)) {
      continue;
    }
    addRow(entry.platforms[row.platform], row);
    addRow(entry.total, row);
  }
  return buckets;
}

/**
 * Whole-number percentages of a split that sum to exactly 100 (largest
 * remainder). Equal remainders go to the earlier entry. `null` when the
 * parts sum to zero.
 */
export function sharesLargestRemainder(counts: Array<number>): Array<number> | null {
  const clean = counts.map((count) => Math.round(finiteCount(count)));
  const total = clean.reduce((sum, count) => sum + count, 0);
  if (total <= 0) {
    return null;
  }
  // Integer math, so equal fractions tie exactly.
  const scaled = clean.map((count) => count * 100);
  const shares = scaled.map((value) => Math.floor(value / total));
  const remainders = scaled.map((value) => value % total);
  let leftover = 100 - shares.reduce((sum, value) => sum + value, 0);
  const byRemainder = clean
    .map((_, index) => index)
    .sort((a, b) => remainders[b] - remainders[a] || a - b);
  for (const index of byRemainder) {
    if (leftover <= 0) {
      break;
    }
    shares[index] += 1;
    leftover -= 1;
  }
  return shares;
}

/**
 * `count` as a whole percentage of `total`, capped at 100. For shares that
 * are not parts of one split (keyword penetration, share of voice).
 */
export function percentOf(count: number, total: number | null | undefined): number | null {
  if (total == null || !Number.isFinite(total) || total <= 0 || !Number.isFinite(count)) {
    return null;
  }
  return Math.min(100, Math.round((Math.max(0, count) / total) * 100));
}

function compareText(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}

/** Net sentiment in points, -100 to 100: (positive - negative) / judged. */
export function netSentimentOf(positive: number, negative: number, judged: number): number | null {
  if (judged <= 0) {
    return null;
  }
  return Math.round(((positive - negative) / judged) * 100);
}

function isPlatform(key: string): key is SocialPlatform {
  return (SOCIAL_PLATFORMS as ReadonlyArray<string>).includes(key);
}

/* ------------------------- Topic page measures -------------------------- */

/**
 * Reach of a count: its summed views. Views come only from posts that report
 * a numeric view count, so a missing, negative or broken value reads 0 (never
 * likes, never NaN).
 */
export function reachOf(measured: { views?: number | null }): number {
  return finiteCount(measured.views);
}

/**
 * One post's views as the server's reach totals count them: a non-negative
 * safe integer, else 0, so a post's share of the reach reads both sides by
 * one rule.
 */
export function postViewsOf(engagement: { views?: number | null } | null | undefined): number {
  const views = engagement?.views;
  return views != null && Number.isSafeInteger(views) && views >= 0 ? views : 0;
}

/** A count's value in the current measure: views for `reach`, posts for `posts`. */
export function measureOf(
  metric: SocialTopicMetric,
  posts: number,
  views: number | null | undefined,
): number {
  return metric === "reach" ? finiteCount(views) : finiteCount(posts);
}

export interface SocialHalfWindow {
  /** First bucket of the second half, `YYYY-MM-DD`: the "since" of "Change: since … vs before". */
  mid: string;
  /** UTC bounds of the first half, for a counts call (creators). */
  previous: SocialWindow;
  /** UTC bounds of the second half. */
  current: SocialWindow;
  previousCounts: SocialSeriesCounts;
  currentCounts: SocialSeriesCounts;
}

function dayStart(day: string): string {
  return `${day}T00:00:00.000Z`;
}

function dayEnd(day: string): string {
  return `${day}T23:59:59.999Z`;
}

function addDays(day: string, days: number): string {
  const ms = utcDayMs(day);
  return ms == null ? day : isoDay(ms + days * DAY_MS);
}

/**
 * The window's two halves by publish date (plan D3): the first and the last
 * floor(n / 2) buckets, so both halves span as many buckets. On an odd count
 * the middle bucket is in neither half. Null under 2 buckets.
 */
export function halfWindowSplit(
  buckets: Array<SocialFilledBucket>,
  bucket: SocialTopicSeriesBucket,
): SocialHalfWindow | null {
  if (buckets.length < 2) {
    return null;
  }
  const half = Math.floor(buckets.length / 2);
  const midIndex = buckets.length - half;
  const mid = buckets[midIndex].start;
  const previousEnd = addDays(buckets[half].start, -1);
  const lastStart = buckets[buckets.length - 1].start;
  const lastDay = bucket === "week" ? addDays(lastStart, 6) : lastStart;
  const previousCounts = emptyCounts();
  const currentCounts = emptyCounts();
  buckets.forEach((entry, index) => {
    const target = index < half ? previousCounts : index >= midIndex ? currentCounts : null;
    if (!target) {
      return;
    }
    (Object.keys(target) as Array<keyof SocialSeriesCounts>).forEach((key) => {
      target[key] += entry.total[key];
    });
  });
  return {
    mid,
    previous: { from: dayStart(buckets[0].start), to: dayEnd(previousEnd) },
    current: { from: dayStart(mid), to: dayEnd(lastDay) },
    previousCounts,
    currentCounts,
  };
}

export interface SocialTopicDelta {
  change: number;
  /**
   * |change| over the previous value, whole percent; null when the previous
   * value is below `MIN_DELTA_PERCENT_BASE` (0 included).
   */
  percent: number | null;
  direction: "up" | "down" | "flat";
}

/**
 * The smallest previous value a delta's percent is shown for. Below it a
 * percent is noise (1 post to 93 reads 9200%), so only the absolute change
 * shows.
 */
export const MIN_DELTA_PERCENT_BASE = 10;

/**
 * A delta from its change and the previous value, the one place the percent
 * rule lives (the Topic page's stat row and a collection's KPI strip).
 * Callers pass finite numbers.
 */
export function socialTopicDelta(change: number, previous: number): SocialTopicDelta {
  return {
    change,
    percent:
      previous >= MIN_DELTA_PERCENT_BASE
        ? Math.round((Math.abs(change) * 100) / previous)
        : null,
    direction: change > 0 ? "up" : change < 0 ? "down" : "flat",
  };
}

/**
 * The second half against the first (posts, creators). Null while a date
 * range is set (D3) or when either half is unknown.
 */
export function halfWindowDelta(input: {
  current: number | null;
  previous: number | null;
  hasWindow: boolean;
}): SocialTopicDelta | null {
  const { current, previous, hasWindow } = input;
  if (hasWindow || current == null || previous == null) {
    return null;
  }
  if (!Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  return socialTopicDelta(current - previous, previous);
}

function shareOf(part: number, whole: number): number | null {
  if (!Number.isFinite(whole) || whole <= 0) {
    return null;
  }
  return Math.min(100, (Math.max(0, finiteCount(part)) * 100) / whole);
}

export interface SocialShareOfVoiceRow {
  brandId: string;
  /** The brand's posts, and their share of the branded posts (0 to 100, unrounded). */
  posts: { value: number; share: number | null };
  /** The brand's views, and their share of the branded views; null when no branded post reports views. */
  reach: { value: number; share: number | null };
  /** The share in the current measure (the filled dot) and in the other (the hollow one). */
  primaryShare: number | null;
  secondaryShare: number | null;
}

export interface SocialShareOfVoice {
  /** By the current measure's share, then posts, then label, then id. Empty when nothing is branded. */
  rows: Array<SocialShareOfVoiceRow>;
  brandedPosts: number;
  brandedViews: number;
}

/**
 * Share of voice per brand over the branded posts (`branded`, H2), in posts
 * and in reach. Each brand is its own share (a post can name two brands), so
 * the rows never pretend to sum to 100.
 */
export function shareOfVoice(input: {
  cells: Array<SocialTopicCountCell>;
  branded: SocialTopicBrandedTotals | null | undefined;
  metric: SocialTopicMetric;
  labelOf?: (brandId: string) => string;
}): SocialShareOfVoice {
  const { cells, branded, metric, labelOf = (key: string) => key } = input;
  const brandedPosts = finiteCount(branded?.count);
  const brandedViews = finiteCount(branded?.views);
  if (brandedPosts <= 0) {
    return { rows: [], brandedPosts, brandedViews };
  }
  const rows = cells.map((cell) => {
    const postsShare = shareOf(cell.count, brandedPosts);
    const reachShare = shareOf(reachOf(cell), brandedViews);
    return {
      brandId: cell.key,
      posts: { value: finiteCount(cell.count), share: postsShare },
      reach: { value: reachOf(cell), share: reachShare },
      primaryShare: metric === "reach" ? reachShare : postsShare,
      secondaryShare: metric === "reach" ? postsShare : reachShare,
    };
  });
  rows.sort(
    (a, b) =>
      (b.primaryShare ?? -1) - (a.primaryShare ?? -1) ||
      b.posts.value - a.posts.value ||
      compareText(labelOf(a.brandId), labelOf(b.brandId)) ||
      compareText(a.brandId, b.brandId),
  );
  return { rows, brandedPosts, brandedViews };
}

/** The D5 name of `shareOfVoice`. */
export const shareOfVoiceRows = shareOfVoice;

/* ------------------------------ Highlights ------------------------------ */

/**
 * The 3 generic Highlights rules, none keyed to a name:
 * - `top-post-reach`: the top post's share of reach;
 * - `loudest-brand`: the loudest brand in the current measure, with its posts vs reach gap;
 * - `negative-channel`: the channel with the largest share of the negative posts, against its share of all posts.
 */
export const SOCIAL_TOPIC_HIGHLIGHT_RULES = [
  "top-post-reach",
  "loudest-brand",
  "negative-channel",
] as const;
export type SocialTopicHighlightRule = (typeof SOCIAL_TOPIC_HIGHLIGHT_RULES)[number];

export interface SocialTopicHighlightsInput {
  metric: SocialTopicMetric;
  /** Any counts under the current filters; only its totals are read. */
  totals: SocialTopicCountsResponse | null;
  /** The most viewed post under the filters (`sort: views, limit: 1`). */
  topPost: SocialTopicPostItem | null;
  /** Brand counts with `branded`, brand filter excluded. */
  brandCounts: SocialTopicCountsResponse | null;
  brandNames: Map<string, string>;
  /** Channel counts, channel filter excluded. */
  channelCounts: SocialTopicCountsResponse | null;
  /** Channel counts of the negative posts, channel filter excluded. */
  negativeChannelCounts: SocialTopicCountsResponse | null;
}

export interface SocialTopicHighlightItem {
  rule: SocialTopicHighlightRule;
  /** What the rule's copy interpolates (`socialListening.topicPage.highlights.<rule>`). */
  values: Record<string, number | string>;
  /** Posts the item cites, most viewed first. */
  citedPostTokens: Array<string>;
  /** What "Filter" applies; null shows no Filter. */
  filter: Pick<SocialTopicFilters, "brandId" | "platform"> | null;
}

/** Fewest posts reporting views before one post's share of reach means anything. */
const TOP_POST_MIN_VIEWS_POSTS = 3;
/** Smallest share of reach the top post needs to be a highlight. */
const TOP_POST_MIN_PERCENT = 15;
/** Fewest branded posts before naming a loudest brand. */
const LOUDEST_BRAND_MIN_BRANDED = 3;
/** Fewest negative posts before naming the channel that carries them. */
const NEGATIVE_CHANNEL_MIN_NEGATIVES = 2;

/** Rule 1: the most viewed post's share of every view under the filters. */
function topPostReach(input: SocialTopicHighlightsInput): SocialTopicHighlightItem | null {
  const { totals, topPost } = input;
  if (!totals || !topPost || (totals.viewsPosts ?? 0) < TOP_POST_MIN_VIEWS_POSTS) {
    return null;
  }
  const views = postViewsOf(topPost.engagement);
  const percent = views > 0 ? percentOf(views, totals.views) : null;
  if (percent == null || percent < TOP_POST_MIN_PERCENT) {
    return null;
  }
  const handle = topPost.authorHandle?.trim().replace(/^@+/, "");
  const author = handle ? `@${handle}` : topPost.providerMeta?.displayName?.trim() ?? "";
  return {
    rule: "top-post-reach",
    values: { author, platform: topPost.platform, views, percent },
    citedPostTokens: [topPost.token],
    filter: null,
  };
}

/** Rule 2: the brand with the largest share of voice in the current measure, with its other share. */
function loudestBrand(input: SocialTopicHighlightsInput): SocialTopicHighlightItem | null {
  const { brandCounts, metric, brandNames, topPost } = input;
  const branded = brandCounts?.branded;
  if (!brandCounts || !branded || branded.count < LOUDEST_BRAND_MIN_BRANDED) {
    return null;
  }
  const named = brandCounts.cells.filter((cell) => cell.count > 0);
  if (named.length < 2) {
    return null;
  }
  const other: SocialTopicMetric = metric === "reach" ? "posts" : "reach";
  const labelOf = (cell: SocialTopicCountCell) => brandNames.get(cell.key) ?? cell.key;
  const [leader] = [...named].sort(
    (a, b) =>
      measureOf(metric, b.count, b.views) - measureOf(metric, a.count, a.views) ||
      measureOf(other, b.count, b.views) - measureOf(other, a.count, a.views) ||
      compareText(labelOf(a), labelOf(b)) ||
      compareText(a.key, b.key),
  );
  if (measureOf(metric, leader.count, leader.views) <= 0) {
    return null;
  }
  const reachPercent = percentOf(reachOf(leader), branded.views);
  const postsPercent = percentOf(leader.count, branded.count);
  if (reachPercent == null || postsPercent == null) {
    return null;
  }
  const citesTopPost = topPost?.brands.some((brand) => brand.id === leader.key) ?? false;
  return {
    rule: "loudest-brand",
    values: { brand: labelOf(leader), brandId: leader.key, reachPercent, postsPercent },
    citedPostTokens: citesTopPost && topPost ? [topPost.token] : [],
    filter: { brandId: leader.key },
  };
}

/** Rule 3: the channel holding the largest share of the negative posts, when above its share of all posts. */
function negativeChannel(input: SocialTopicHighlightsInput): SocialTopicHighlightItem | null {
  const { negativeChannelCounts, channelCounts, topPost } = input;
  if (
    !negativeChannelCounts ||
    !channelCounts ||
    negativeChannelCounts.total < NEGATIVE_CHANNEL_MIN_NEGATIVES
  ) {
    return null;
  }
  const order = (key: string) => (SOCIAL_PLATFORMS as ReadonlyArray<string>).indexOf(key);
  const [top] = negativeChannelCounts.cells
    .filter((cell) => isPlatform(cell.key) && cell.count > 0)
    .sort((a, b) => b.count - a.count || order(a.key) - order(b.key));
  if (!top || !isPlatform(top.key)) {
    return null;
  }
  const negativeShare = percentOf(top.count, negativeChannelCounts.total);
  const channelCount = channelCounts.cells.find((cell) => cell.key === top.key)?.count ?? 0;
  const postsShare = percentOf(channelCount, channelCounts.total);
  if (negativeShare == null || postsShare == null || negativeShare <= postsShare) {
    return null;
  }
  const citesTopPost = topPost?.platform === top.key && topPost.sentiment === "negative";
  return {
    rule: "negative-channel",
    values: {
      platform: top.key,
      count: top.count,
      total: negativeChannelCounts.total,
      percent: postsShare,
    },
    citedPostTokens: citesTopPost && topPost ? [topPost.token] : [],
    filter: { platform: top.key },
  };
}

/**
 * The Highlights that hold under the filters, at most one per rule, in rule
 * order; a rule without enough data returns nothing, never n/a or NaN.
 * `values` are raw: `platform` is a SocialPlatform key and `views` a count,
 * for the component to label and format; `author` is `@handle`, else the
 * display name, else "".
 */
export function socialTopicHighlights(
  input: SocialTopicHighlightsInput,
): Array<SocialTopicHighlightItem> {
  return [topPostReach(input), loudestBrand(input), negativeChannel(input)].filter(
    (item): item is SocialTopicHighlightItem => item != null,
  );
}

/** The D5 name of `socialTopicHighlights`. */
export const topicHighlights = socialTopicHighlights;
